import { useEffect, useRef, useState } from "react";
import { Mail, ArrowRight, X } from "lucide-react";
import { useSession } from "../lib/SessionContext";

/**
 * Soft email gate – no password, no verification. Opens on first
 * visit (with a Skip option) and from the nav "Sign in" button.
 *
 * 2026-05-26 UX cleanup: removed the inline waitlist opt-in
 * checkbox. The identify modal now does ONE thing. collect an
 * email. The waitlist + newsletter consent has moved to a single
 * confirmation moment AFTER the user has actually seen Owl in
 * action (chat drawer, after the 3rd Owl answer). Two reasons:
 *   • Honest consent: asking "want the waitlist?" before the user
 *     has experienced the product is a guess. Asking after means
 *     they're saying yes based on real value, not a pitch.
 *   • Clean separation: identify = one ask, waitlist = a separate,
 *     considered second ask. No piggy-back checkboxes.
 */
export function IdentifyModal({
  open,
  onClose,
  context = "first-visit",
}: {
  open: boolean;
  onClose: () => void;
  context?: "first-visit" | "explicit";
}) {
  const { identify, dismissPrompt } = useSession();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    setEmail("");
    setError(null);
    setBusy(false);
  }, [open]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (open && e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true);
    setError(null);
    const res = await identify(email);
    if (!res.ok) {
      setBusy(false);
      setError(res.error ?? "Couldn't sign you in.");
      return;
    }
    setBusy(false);
    onClose();
  }

  function handleSkip() {
    dismissPrompt();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-60 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="identify-title"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={context === "first-visit" ? handleSkip : onClose}
        className="absolute inset-0 bg-nyvo-950/60 backdrop-blur-xs"
      />
      <div className="relative z-10 w-full max-w-md animate-fade-in rounded-2xl border border-slate-200 bg-white p-8 shadow-2xl shadow-nyvo-900/25">
        <button
          type="button"
          aria-label="Close"
          onClick={context === "first-visit" ? handleSkip : onClose}
          className="absolute right-3 top-3 rounded-sm p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>

        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-nyvo-700">
          <Mail className="h-3.5 w-3.5" aria-hidden />
          {context === "first-visit" ? "Join the waitlist" : "Continue with email"}
        </div>
        <h2 id="identify-title" className="headline mt-3 text-[1.75rem] leading-tight">
          {context === "first-visit"
            ? "Be first to get NYVO Money"
            : "What's your email?"}
        </h2>
        {/* 2026-05-28 – copy revised. Old line said "no marketing" but
            /identify now auto-enrols every email into the NYVO Money
            waitlist + weekly newsletter (per product direction). Be
            honest: name what happens. The opt-out confirmation appears
            in the chat drawer after the user has gotten value (3+
            Owl answers) where they can uncheck to leave the list. */}
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          {context === "first-visit"
            ? "Drop your email to join the NYVO Money waitlist and get the weekly newsletter. We'll also save your questions so you can pick up where you left off. No password, no verification — opt out anytime. Or skip and keep browsing anonymously."
            : "We save your questions so you can come back to them, and add you to the NYVO Money waitlist + weekly newsletter. You can opt out anytime from the chat panel after a few answers. No password, no verification. Skip to stay anonymous."}
        </p>

        <form onSubmit={submit} className="mt-5">
          <input
            ref={inputRef}
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            placeholder="you@yourcompany.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-slate-300 bg-white/95 px-4 py-3.5 text-base text-nyvo-900 placeholder:text-slate-400 backdrop-blur-sm focus:border-nyvo-500 focus:outline-hidden focus:ring-2 focus:ring-nyvo-200"
          />
          {error && (
            <p className="mt-2 text-xs text-red-700" role="alert">
              {error}
            </p>
          )}

          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            {context === "first-visit" ? (
              <button
                type="button"
                onClick={handleSkip}
                className="text-xs text-slate-500 transition hover:text-slate-800"
              >
                Skip – keep me anonymous
              </button>
            ) : (
              <span aria-hidden />
            )}
            <button
              type="submit"
              disabled={busy || email.trim().length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-nyvo-700 px-5 py-3 text-sm font-bold tracking-wide text-white shadow-md shadow-nyvo-900/15 transition hover:bg-nyvo-800 disabled:opacity-50"
            >
              {busy
                ? "Saving…"
                : context === "first-visit" ? "Join the waitlist" : "Continue"}
              {!busy && <ArrowRight className="h-3.5 w-3.5" aria-hidden />}
            </button>
          </div>
        </form>

        <p className="mt-5 text-[10px] leading-relaxed text-slate-400">
          By continuing you agree we can save your queries and add you
          to the NYVO Money waitlist + newsletter. Opt out anytime from
          the chat panel. We never sell or share your email.
        </p>
      </div>
    </div>
  );
}
